import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';
import { shouldShowNumericFullTooltip } from '../../utils/FormatUtils';

export type NumericValueWithTooltipProps = {
  /** Rounded or abbreviated value shown in the cell */
  display: string;
  /** Complete value shown on hover when it differs from `display` */
  fullValue: string;
  variant?: TypographyProps['variant'];
  typographySx?: SxProps<Theme>;
  align?: 'left' | 'center' | 'right' | 'inherit';
  emptyLabel?: string;
};

/**
 * Renders a numeric display string; when the value was rounded or compacted,
 * hovering shows the full precision value.
 */
export function NumericValueWithTooltip({
  display,
  fullValue,
  variant = 'body2',
  typographySx,
  align = 'inherit',
  emptyLabel = '',
}: NumericValueWithTooltipProps) {
  const trimmedDisplay = (display ?? '').trim();
  const show = trimmedDisplay.length > 0 ? trimmedDisplay : emptyLabel;
  const showTooltip = shouldShowNumericFullTooltip(show, fullValue);

  const body = (
    <Typography
      component="span"
      variant={variant}
      sx={{
        display: 'block',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: align,
        ...typographySx,
      }}
    >
      {show}
    </Typography>
  );

  if (!show || !showTooltip) {
    return body;
  }

  return (
    <Tooltip
      title={fullValue.trim()}
      arrow
      placement="top"
      enterDelay={300}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 480,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          },
        },
      }}
    >
      <Box component="span" sx={{ display: 'block', minWidth: 0, maxWidth: '100%', cursor: 'default' }}>
        {body}
      </Box>
    </Tooltip>
  );
}
